// LUL-653 — Notification inbox + admin API (typed NotificationAdminGrpcService).
// Account/tenant lấy từ JWT phía server — FE không truyền id người dùng.

import { Injectable } from "@angular/core";
import { BehaviorSubject, EMPTY, expand, from, last, map, Observable, of, tap, timeout } from "rxjs";
import { RpcOptions } from "@protobuf-ts/runtime-rpc";
import { VlpGrpcClients } from "shared-service/services/grpc/grpc-client.factory";
import { ExternalNotificationAdminRPCClient } from "shared-grpc-api/shared/external-apis/vlp-notification/vlp-notification-admin-external-apis.client";
import { Timestamp } from "shared-grpc-api/google/protobuf/timestamp";
import {
    AlertIncidentModel,
    NotificationDefinitionModel,
    NotificationInboxItem,
    NotificationInboxTypeOption,
    NotificationMailProfileModel,
} from "shared-domains/domains/notification/notification.models";
import { PagedResult } from "app/core/services/grid/vlp-grid-store.factory";

@Injectable({ providedIn: "root" })
export class NotificationInboxService {
    private static readonly READ_TIMEOUT_MS = 8_000;

    // -----------------------------------------------------------------------------------------------------
    // @ Private fields — reactive state & lazy gRPC client
    // -----------------------------------------------------------------------------------------------------
    private _client?: ExternalNotificationAdminRPCClient;
    private get client(): ExternalNotificationAdminRPCClient {
        return (this._client ??= new ExternalNotificationAdminRPCClient(this._grpc.notification()));
    }

    /** Unread count realtime cho bell badge (poll + cập nhật sau mark read). */
    private readonly _unreadCount = new BehaviorSubject<number>(0);
    private readonly _readCount = new BehaviorSubject<number>(0);
    private readonly _totalCount = new BehaviorSubject<number>(0);
    private readonly _typeOptions = new BehaviorSubject<NotificationInboxTypeOption[]>([]);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    readonly unreadCount$ = this._unreadCount.asObservable();
    readonly readCount$ = this._readCount.asObservable();
    readonly totalCount$ = this._totalCount.asObservable();
    readonly typeOptions$ = this._typeOptions.asObservable();

    setRealtimeUnreadCount(count: number): void {
        this._unreadCount.next(Math.max(0, Number(count ?? 0)));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------
    constructor(private _grpc: VlpGrpcClients) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — Read / Query
    // -----------------------------------------------------------------------------------------------------

    // ── Inbox (AC 7: persistent, unread count, mark read, deep-link) ───────────────────────────
    listMyNotifications(unreadOnly = false, skip = 0, take = 50): Observable<NotificationInboxItem[]> {
        return this.cancellableRead((options) => this.client.listMyNotifications({
            unreadOnly, readOnly: false, keyword: "", severity: 0, definitionCode: "", skip, take,
        }, options).response).pipe(
            tap((value: any) => this.updateInboxMetadata(value)),
            map((response: any) => {
                const messages = new Map<number, any>((response.items ?? []).map((model: any) => [Number(model.notificationMessageId), model]));
                return (response.deliveries ?? []).map((dto: any) =>
                    new NotificationInboxItem(messages.get(Number(dto.notificationMessageId)), dto));
            }),
        );
    }

    listMyNotificationsPage(
        readState: boolean | null,
        keyword: string,
        severity: number,
        definitionCode: string,
        skip: number,
        take: number,
    ): Observable<PagedResult<NotificationInboxItem>> {
        return this.cancellableRead((options) => this.client.listMyNotifications({
            unreadOnly: readState === true,
            readOnly: readState === false,
            keyword: keyword?.trim() ?? "",
            severity: Number(severity ?? 0),
            definitionCode: definitionCode?.trim() ?? "",
            skip,
            take,
        }, options).response).pipe(
            tap((value) => this.updateInboxMetadata(value)),
            map((response) => {
                const messages = new Map<number, any>(
                    (response.items ?? []).map((model) => [Number(model.notificationMessageId), model]),
                );
                return {
                    data: (response.deliveries ?? []).map((dto) =>
                        new NotificationInboxItem(
                            messages.get(Number(dto.notificationMessageId)),
                            dto,
                        )),
                    totalCount: Number(response.totalCount ?? 0),
                };
            }),
        );
    }

    // ── Admin: catalog + incident ──────────────────────────────────────────────────────────────
    listDefinitions(): Observable<NotificationDefinitionModel[]> {
        return from(this.client.listNotificationDefinitions({ category: 0, includeArchived: false } as any).response).pipe(
            map((response: any) => (response.items ?? []).map((dto: any) => new NotificationDefinitionModel(dto))),
        );
    }

    listRules(definitionId = 0): Observable<any[]> {
        return from(this.client.listNotificationRules({
            notificationDefinitionId: definitionId, includePlatformDefaults: true,
        } as any).response).pipe(map((response: any) => response.items ?? []));
    }

    listTemplates(definitionId: number): Observable<any[]> {
        return from(this.client.listNotificationTemplates({ notificationDefinitionId: definitionId } as any).response).pipe(
            map((response: any) => response.items ?? []),
        );
    }

    listSilences(): Observable<any[]> {
        return from(this.client.listAlertSilences({ id: 0 } as any).response).pipe(map((response: any) => response.items ?? []));
    }

    listIncidents(state = 0): Observable<AlertIncidentModel[]> {
        return from(this.client.listAlertIncidents({ state, severity: 0, skip: 0, take: 100 } as any).response).pipe(
            map((response: any) => (response.items ?? []).map((i: any) => new AlertIncidentModel(i))),
        );
    }

    /** Find an older incident through the existing scoped, descending-ID API, not only its first page. */
    findIncident(id: number): Observable<AlertIncidentModel[]> {
        if (!Number.isSafeInteger(id) || id <= 0) return of([]);
        const page = (skip: number) => this.cancellableRead(options => this.client.listAlertIncidents({
            state: 0, severity: 0, skip, take: 100,
        }, options).response).pipe(map(response => {
            const failure = NotificationInboxService.failureOf(response.status, 'INCIDENT_LOOKUP_FAILED');
            if (failure) throw failure;
            return (response.items ?? []).map(item => new AlertIncidentModel(item));
        }));
        return page(0).pipe(
            expand((items, index) => items.length === 100 && items[items.length - 1].AlertIncidentId > id
                && !items.some(item => item.AlertIncidentId === id) ? page((index + 1) * 100) : EMPTY),
            last(),
            map(items => items.filter(item => item.AlertIncidentId === id)),
            timeout(30_000),
        );
    }

    listDeliveryHistory(definitionId = 0, skip = 0, take = 200): Observable<any[]> {
        return from(this.client.listDeliveryHistory({ notificationMessageId: 0, notificationDefinitionId: definitionId, skip, take } as any).response).pipe(
            map((response: any) => response.items ?? []),
        );
    }

    listMailProfiles(organizationUnitId = 0): Observable<NotificationMailProfileModel[]> {
        return from(this.client.listNotificationMailProfiles({
            organizationUnitId,
            includeDisabled: true,
        }).response).pipe(
            map((response) => (response.items ?? []).map((dto) => new NotificationMailProfileModel(dto))),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — Mutations
    // -----------------------------------------------------------------------------------------------------

    markRead(deliveryId: number): Observable<number> {
        return from(this.client.markNotificationRead({ notificationDeliveryId: deliveryId, markAll: false } as any).response).pipe(
            tap((value: any) => {
                this._unreadCount.next(Number(value.unreadCount ?? 0));
                if (Number(value.markedCount ?? 0) > 0) {
                    this._readCount.next(this._readCount.value + Number(value.markedCount));
                }
            }),
            map((response: any) => Number(response.markedCount ?? 0)),
        );
    }

    markAllRead(): Observable<number> {
        return from(this.client.markNotificationRead({ notificationDeliveryId: 0, markAll: true } as any).response).pipe(
            tap((value: any) => this._unreadCount.next(Number(value.unreadCount ?? 0))),
            map((response: any) => Number(response.markedCount ?? 0)),
        );
    }

    deleteReadNotifications(): Observable<number> {
        return from(this.client.deleteMyReadNotifications({}).response).pipe(
            tap((value: any) => {
                this._unreadCount.next(Number(value.unreadCount ?? 0));
                this._readCount.next(Number(value.readCount ?? 0));
            }),
            map((response: any) => Number(response.deletedCount ?? 0)),
        );
    }

    saveRule(rule: any): Observable<any | null> {
        return from(this.client.addOrUpdateNotificationRule(rule as any).response).pipe(map((response: any) => response.data ?? null));
    }

    saveRecipientRule(recipient: any): Observable<any | null> {
        return from(this.client.addOrUpdateRecipientRule(recipient as any).response).pipe(
            map((response: any) => response.data ?? null),
        );
    }

    removeRecipientRule(recipientRuleId: number): Observable<boolean> {
        return from(this.client.removeRecipientRule({ id: recipientRuleId } as any).response).pipe(
            map((response: any) => Boolean(response.data)),
        );
    }

    /**
     * Chỉ platform actor được sửa mẫu; tenant khác nhận NOTIFICATION_PLATFORM_TEMPLATE_REQUIRED trong
     * `status`, nên phải kiểm tra ở đây thay vì để nó lặng lẽ thành "đã lưu" — cùng lớp lỗi với nút
     * gửi thư kiểm tra và nút lưu cấu hình email.
     */
    saveTemplate(template: any): Observable<any | null> {
        return from(this.client.addOrUpdateNotificationTemplate(template as any).response).pipe(
            map((response: any) => {
                const failure = NotificationInboxService.failureOf(response.status, "NOTIFICATION_TEMPLATE_SAVE_FAILED");
                if (failure) {
                    throw failure;
                }

                return response.data ?? null;
            }),
        );
    }

    saveSilence(silence: any): Observable<any | null> {
        const request = {
            ...silence,
            fromAt: silence.fromAt ? Timestamp.fromDate(new Date(silence.fromAt)) : undefined,
            toAt: silence.toAt ? Timestamp.fromDate(new Date(silence.toAt)) : undefined,
        };
        return from(this.client.addOrUpdateAlertSilence(request as any).response).pipe(map((response: any) => response.data ?? null));
    }

    /**
     * Rejection of a mail-profile write travels in <code>status</code>, not as an RPC error, so a caller
     * that only reads <code>data</code> treats every refusal as a save. Same defect as testMailProfile
     * had, one step earlier: the screen says "đã lưu cấu hình email" while the password never reached
     * the server, and the next test then fails with MAIL_CREDENTIAL_REQUIRED for no visible reason.
     */
    private static failureOf(
        status: { success?: boolean; resultCode?: string; message?: string } | undefined,
        fallback: string): (Error & { resultCode?: string }) | null {
        if (status?.success !== false) {
            return null;
        }

        const code = status.resultCode ?? "";
        const error = new Error(status.message || code || fallback) as Error & { resultCode?: string };
        error.resultCode = code;
        return error;
    }

    saveMailProfile(profile: NotificationMailProfileModel): Observable<NotificationMailProfileModel | null> {
        return from(this.client.addOrUpdateNotificationMailProfile(profile.toDto()).response).pipe(
            map((response) => {
                const failure = NotificationInboxService.failureOf(response.status, "MAIL_PROFILE_SAVE_FAILED");
                if (failure) {
                    throw failure;
                }

                return response.data ? new NotificationMailProfileModel(response.data) : null;
            }),
        );
    }

    saveMailCredential(profileId: number, password: string): Observable<NotificationMailProfileModel | null> {
        return from(this.client.saveNotificationMailCredential({
            notificationMailProfileId: profileId,
            password,
        }).response).pipe(
            map((response) => {
                const failure = NotificationInboxService.failureOf(response.status, "MAIL_CREDENTIAL_SAVE_FAILED");
                if (failure) {
                    throw failure;
                }

                return response.data ? new NotificationMailProfileModel(response.data) : null;
            }),
        );
    }

    /**
     * Sends a test message through a mail profile.
     *
     * <p>THROWS when the server says the send failed. It used to read only <code>resultCode</code> and
     * ignore <code>status</code>, so every outcome reached the caller's success branch and the screen
     * reported "đã gửi thư kiểm tra" for a profile whose SMTP handshake had just been refused — the one
     * message a person uses to decide the mail server is configured, saying yes when the answer was no.</p>
     *
     * <p>The backend already distinguishes the cases it can act on (MAIL_CREDENTIAL_REQUIRED,
     * CALLER_EMAIL_NOT_AVAILABLE, SMTP_*), so its code is carried on the error for the caller to translate
     * rather than flattened into one generic failure.</p>
     */
    testMailProfile(profileId: number): Observable<string> {
        return from(this.client.testNotificationMailProfile({
            notificationMailProfileId: profileId,
            sendTestMessage: true,
        }).response).pipe(
            map((response) => {
                const code = response.resultCode ?? "";
                if (response.status?.success === false) {
                    const error = new Error(
                        response.status?.message || code || "MAIL_TEST_FAILED",
                    ) as Error & { resultCode?: string };
                    error.resultCode = code;
                    throw error;
                }

                return code;
            }),
        );
    }

    acknowledgeIncident(incidentId: number, note: string): Observable<AlertIncidentModel | null> {
        return from(this.client.acknowledgeIncident({ alertIncidentId: incidentId, note } as any).response).pipe(
            map((response: any) => (response.data ? new AlertIncidentModel(response.data) : null)),
        );
    }

    resolveIncident(incidentId: number, resolution: string): Observable<AlertIncidentModel | null> {
        return from(this.client.resolveIncident({
            alertIncidentId: incidentId, resolution, rcaReference: "",
        } as any).response).pipe(map((response: any) => (response.data ? new AlertIncidentModel(response.data) : null)));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — khác (test send)
    // -----------------------------------------------------------------------------------------------------

    testSend(definitionId: number, channel: number, language: string, payloadJson: string): Observable<{ subject: string; body: string }> {
        return from(this.client.testSendNotification({ notificationDefinitionId: definitionId, channel, language, samplePayloadJson: payloadJson } as any).response).pipe(
            map((response: any) => ({ subject: response.renderedSubject ?? "", body: response.renderedBody ?? "" })),
        );
    }

    /**
     * Wrap a protobuf-ts unary read so RxJS unsubscription really aborts the underlying fetch.
     * `from(call.response)` alone only detaches the observer and leaves the HTTP request alive.
     */
    private cancellableRead<T>(factory: (options: RpcOptions) => Promise<T>): Observable<T> {
        return new Observable<T>((subscriber) => {
            const controller = new AbortController();
            let settled = false;

            factory({
                abort: controller.signal,
                timeout: NotificationInboxService.READ_TIMEOUT_MS,
            }).then(
                (value) => {
                    settled = true;
                    if (!subscriber.closed) {
                        subscriber.next(value);
                        subscriber.complete();
                    }
                },
                (error) => {
                    settled = true;
                    if (!subscriber.closed) subscriber.error(error);
                },
            );

            return () => {
                if (!settled) controller.abort();
            };
        });
    }

    private updateInboxMetadata(value: any): void {
        this._unreadCount.next(Number(value?.unreadCount ?? 0));
        this._readCount.next(Number(value?.readCount ?? 0));
        this._totalCount.next(Number(value?.totalCount ?? 0));
        this._typeOptions.next(
            (value?.typeOptions ?? [])
                .map((item: any) => new NotificationInboxTypeOption(item))
                .filter((item: NotificationInboxTypeOption) => item.DefinitionCode.length > 0),
        );
    }
}
